/**
 * @file: erp-integration.service.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { HttpClient } from '@euler/lib/request';
import { ErpCreateOrderRequest } from '@euler/model';
import { CursorResult } from '@euler/model/common';
import {
  StoreErpIntegration,
  StoreErpOrderSyncLog,
  VehicleInspectionTask,
  VehicleReceptionOrder,
} from '@euler/model/entity';
import { ErpProviderId } from '@euler/model/enum';
import {
  ChromeExtensionErpIntegrationSettings,
  ErpConnectionEmployeeMappings,
  ErpConnectionInfoList,
  ErpHostedLoginResult,
  ErpLoginCredentials,
  ErpOrderSyncLogSearchParams,
  ErpOrderSyncStatistics,
  ErpOutboundOrderSyncLogRequest,
  ErpProviderInfo,
} from '@euler/model/erp-integrations';

export class StoreErpIntegrationService {
  constructor(private readonly api: HttpClient) {}

  //#region browser extension
  // Frozen surface. These routes predate the connection/integration model and
  // the Chrome extension updates on its own schedule, so they keep working
  // unchanged. New work belongs on the integration-scoped calls below.

  async getBrowserExtensionErpIntegration(): Promise<
    StoreErpIntegration | null | undefined
  > {
    return await this.api
      .get()
      .url('/erp-integrations/browser-extension')
      .future();
  }

  async saveBrowserExtensionErpIntegrationSettings(
    erpProviderId: ErpProviderId,
    settings: ChromeExtensionErpIntegrationSettings,
  ): Promise<StoreErpIntegration | null | undefined> {
    return await this.api
      .post()
      .url('/erp-integrations/browser-extension')
      .data({ erpProviderId, settings })
      .future();
  }

  async getBrowserExtensionErpIntegrationEmployeeMappings(): Promise<ErpConnectionEmployeeMappings> {
    return await this.api
      .get()
      .url('/erp-integrations/browser-extension/employee-mappings')
      .future();
  }

  async saveBrowserExtensionErpIntegrationEmployeeMappings(
    mappings: ErpConnectionEmployeeMappings,
  ): Promise<void> {
    await this.api
      .put()
      .url('/erp-integrations/browser-extension/employee-mappings')
      .data({ mappings })
      .future();
  }

  async removeBrowserExtensionErpIntegration() {
    await this.api.delete().url('/erp-integrations/browser-extension').future();
  }

  async activateBrowserExtensionErpIntegration() {
    await this.api
      .patch()
      .url('/erp-integrations/browser-extension/activate')
      .future();
  }

  async deactivateBrowserExtensionErpIntegration() {
    await this.api
      .patch()
      .url('/erp-integrations/browser-extension/deactivate')
      .future();
  }

  async logErpBrowserExtensionOutboundOrderSyncResult(
    request: ErpOutboundOrderSyncLogRequest,
  ) {
    await this.api
      .post()
      .url('/erp-integrations/browser-extension/outbound-order-sync-logs')
      .data({ request })
      .future();
  }

  //#endregion

  /**
   * Creates an order pulled out of an ERP. `request.source` names both the
   * mechanism and the integration it came through, so the order is not filed
   * against whichever integration the store happens to have first.
   */
  async syncOrderFromErp(
    request: ErpCreateOrderRequest,
    args?: { __fake__?: boolean; __simulatefailure__?: boolean },
  ): Promise<{ task: VehicleInspectionTask; order: VehicleReceptionOrder }> {
    return await this.api
      .post()
      .url('/erp-integrations/orders', args)
      .data({ request })
      .future();
  }

  /**
   * The provider catalog. Static per deploy, so callers cache it rather than
   * fetching per screen — see useErpProviders.
   */
  async getErpProviders(): Promise<ErpProviderInfo[]> {
    return await this.api.get().url('/erp-integrations/providers').future();
  }

  /** Every ERP connection this store has, each with its live integrations. */
  async getErpConnections(): Promise<ErpConnectionInfoList> {
    return await this.api.get().url('/erp-integrations/connections').future();
  }

  /**
   * Starts a hosted login. Returns the id to poll with
   * `getErpLoginStatus` — the browser work runs server-side and outlives
   * this request. Throttled to one attempt per store per 30s.
   */
  async submitErpLoginCredential(
    providerId: ErpProviderId,
    credentials: ErpLoginCredentials,
    version?: string,
  ): Promise<{ sessionId: string }> {
    return await this.api
      .post()
      .url(`/erp-integrations/${providerId}/login`)
      .data({ credentials, version })
      .future();
  }

  /**
   * Polls one login attempt. On success the result carries the
   * `integrationId` that every call below is addressed by. An attempt that
   * outlives its five minute window reads as failed.
   */
  async getErpLoginStatus(sessionId: string): Promise<ErpHostedLoginResult> {
    return await this.api
      .get()
      .url(`/erp-integrations/login-status/${sessionId}`)
      .future();
  }

  /**
   * Integration-scoped lifecycle, so a hosted integration can be turned off
   * or removed. The browser-extension routes above stay for the extension.
   */
  async deactivateErpIntegration(integrationId: string): Promise<void> {
    await this.api
      .patch()
      .url(`/erp-integrations/integrations/${integrationId}/deactivate`)
      .future();
  }

  async removeErpIntegration(integrationId: string): Promise<void> {
    await this.api
      .delete()
      .url(`/erp-integrations/integrations/${integrationId}`)
      .future();
  }

  async getErpEmployees(integrationId: string): Promise<any[]> {
    return await this.api
      .get()
      .url(`/erp-integrations/integrations/${integrationId}/employees`)
      .future();
  }

  async pullOrdersFromErp(
    integrationId: string,
    startTime: string,
    endTime: string,
  ): Promise<any[]> {
    return await this.api
      .get()
      .url(`/erp-integrations/integrations/${integrationId}/orders`, {
        startTime,
        endTime,
      })
      .future();
  }

  async pushOrderToErp(
    integrationId: string,
    orderData: any,
  ): Promise<{ resultOrderno?: string }> {
    return await this.api
      .post()
      .url(`/erp-integrations/integrations/${integrationId}/orders/push`)
      .data({ orderData })
      .future();
  }

  /** Mechanism-agnostic: the integration is named by `request.integrationId`. */
  async logErpOutboundOrderSyncResult(request: ErpOutboundOrderSyncLogRequest) {
    await this.api
      .post()
      .url('/erp-integrations/outbound-order-sync-logs')
      .data({ request })
      .future();
  }

  async searchErpOrderSyncLogs(
    params: ErpOrderSyncLogSearchParams,
    sortDir: 'asc' | 'desc',
    nextKey: string | undefined,
    limit: number,
  ): Promise<CursorResult<StoreErpOrderSyncLog, string>> {
    return await this.api
      .post()
      .url('/erp-integrations/order-sync-logs/search')
      .data({ params, sortDir, nextKey, limit })
      .future();
  }

  async getErpOrderSyncStatistics(
    params: ErpOrderSyncLogSearchParams,
  ): Promise<ErpOrderSyncStatistics> {
    return await this.api
      .post()
      .url('/erp-integrations/order-sync-logs/statistics')
      .data({ params })
      .future();
  }
}
